import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { 
  Lock, 
  Building2, 
  Hospital, 
  DollarSign, 
  Loader2, 
  LogOut,
  RefreshCw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [hotelPartners, setHotelPartners] = useState([]);
  const [hospitalPartners, setHospitalPartners] = useState([]);
  const [donations, setDonations] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        toast.success("Login successful");
        loadData();
      }
    } catch (error) {
      toast.error("Invalid password");
    } finally {
      setIsLoading(false);
    }
  };

  const loadData = async () => {
    setDataLoading(true);
    try {
      const [dashRes, hotelsRes, hospitalsRes, donationsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`),
        axios.get(`${API}/partners/hotels`),
        axios.get(`${API}/partners/hospitals`),
        axios.get(`${API}/donations`)
      ]);

      setDashboardData(dashRes.data);
      setHotelPartners(hotelsRes.data);
      setHospitalPartners(hospitalsRes.data);
      setDonations(donationsRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load data");
    } finally {
      setDataLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword("");
    setDashboardData(null);
    setHotelPartners([]);
    setHospitalPartners([]);
    setDonations([]);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      new: "bg-blue-100 text-blue-800",
      contacted: "bg-yellow-100 text-yellow-800",
      partnered: "bg-green-100 text-green-800",
      declined: "bg-red-100 text-red-800",
      paid: "bg-green-100 text-green-800",
      pending: "bg-yellow-100 text-yellow-800",
      failed: "bg-red-100 text-red-800"
    };
    return (
      <Badge className={variants[status] || "bg-gray-100 text-gray-800"}>
        {status}
      </Badge>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center py-24 px-6">
        <Card className="max-w-md w-full bg-card border-border/50">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="font-serif text-2xl">Admin Access</CardTitle>
            <CardDescription>
              Enter the admin password to access the dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="input-focus"
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
                data-testid="admin-login-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  "Login"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="py-12 px-6 lg:px-12 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-serif text-3xl font-bold text-foreground">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-1">Manage partners and view donations</p>
        </div>
        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={loadData}
            disabled={dataLoading}
            className="rounded-full"
            data-testid="refresh-data-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${dataLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="rounded-full"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-card border-border/50" data-testid="stat-hotel-partners">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {dashboardData.hotel_partners}
                  </div>
                  <div className="text-sm text-muted-foreground">Hotel Inquiries</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border/50" data-testid="stat-hospital-partners">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Hospital className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {dashboardData.hospital_partners}
                  </div>
                  <div className="text-sm text-muted-foreground">Hospital Inquiries</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border/50" data-testid="stat-donation-count">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {dashboardData.donation_count}
                  </div>
                  <div className="text-sm text-muted-foreground">Donations</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border/50" data-testid="stat-total-donations">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    ${dashboardData.total_donations?.toFixed(2) || "0.00"}
                  </div>
                  <div className="text-sm text-muted-foreground">Total Raised</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="hotels" className="space-y-6">
        <TabsList className="bg-secondary">
          <TabsTrigger value="hotels" data-testid="tab-hotels">Hotel Partners</TabsTrigger>
          <TabsTrigger value="hospitals" data-testid="tab-hospitals">Hospital Partners</TabsTrigger>
          <TabsTrigger value="donations" data-testid="tab-donations">Donations</TabsTrigger>
        </TabsList>

        <TabsContent value="hotels">
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle>Hotel Partner Inquiries</CardTitle>
              <CardDescription>
                All hotel partnership inquiries submitted through the website.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {hotelPartners.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No hotel inquiries yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Hotel</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Rooms</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {hotelPartners.map((partner) => (
                        <TableRow key={partner.id} data-testid={`hotel-row-${partner.id}`}>
                          <TableCell className="font-medium">{partner.hotel_name}</TableCell>
                          <TableCell>
                            <div>{partner.contact_name}</div>
                            <div className="text-sm text-muted-foreground">{partner.email}</div>
                          </TableCell>
                          <TableCell>{partner.city}, {partner.state}</TableCell>
                          <TableCell>{partner.rooms_available || "-"}</TableCell>
                          <TableCell>{getStatusBadge(partner.status)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(partner.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="hospitals">
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle>Hospital Partner Inquiries</CardTitle>
              <CardDescription>
                All hospital partnership inquiries submitted through the website.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {hospitalPartners.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No hospital inquiries yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Hospital</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {hospitalPartners.map((partner) => (
                        <TableRow key={partner.id} data-testid={`hospital-row-${partner.id}`}>
                          <TableCell className="font-medium">{partner.hospital_name}</TableCell>
                          <TableCell>
                            <div>{partner.contact_name}</div>
                            <div className="text-sm text-muted-foreground">{partner.email}</div>
                          </TableCell>
                          <TableCell>{partner.city}, {partner.state}</TableCell>
                          <TableCell>{partner.department || "-"}</TableCell>
                          <TableCell>{getStatusBadge(partner.status)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(partner.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="donations">
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle>Donations</CardTitle>
              <CardDescription>
                All donation transactions processed through the website.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {donations.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No donations yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Amount</TableHead>
                        <TableHead>Donor</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {donations.map((donation) => (
                        <TableRow key={donation.id} data-testid={`donation-row-${donation.id}`}>
                          <TableCell className="font-medium">
                            ${donation.amount?.toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <div>{donation.donor_name || "Anonymous"}</div>
                            {donation.donor_email && (
                              <div className="text-sm text-muted-foreground">
                                {donation.donor_email}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="max-w-xs truncate">
                            {donation.message || "-"}
                          </TableCell>
                          <TableCell>{getStatusBadge(donation.payment_status)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(donation.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
